import { useState, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { useAuth } from '@/contexts/AuthContext'
import { apiBulkRemindSuppliers, type BulkRemindResponse } from '@/lib/api'

const DEFAULT_SUBJECT = 'Relance — action requise'
const DEFAULT_MESSAGE =
  "Bonjour,\n\nNous nous permettons de revenir vers vous concernant le formulaire de collecte de données environnementales en attente de votre part. Merci de le compléter dans les meilleurs délais pour nous permettre de finaliser notre analyse.\n\nEn cas de difficulté, n'hésitez pas à nous contacter directement."
const SUBJECT_MAX = 200
const MESSAGE_MAX = 2000
const MESSAGE_WARN_THRESHOLD = 1800

type Props = {
  selectedSupplierIds: string[]
  selectedSupplierNames: string[]
  memberName: string
  memberEmail: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: (result: BulkRemindResponse) => void
}

export function BulkRemindModal({
  selectedSupplierIds,
  selectedSupplierNames,
  memberName,
  memberEmail,
  open,
  onOpenChange,
  onSuccess,
}: Props) {
  const { logout } = useAuth()
  const [subject, setSubject] = useState(DEFAULT_SUBJECT)
  const [message, setMessage] = useState(DEFAULT_MESSAGE)
  const [sending, setSending] = useState(false)
  const requestSeqRef = useRef(0)

  const count = selectedSupplierIds.length
  const subjectValid = subject.trim().length > 0 && subject.length <= SUBJECT_MAX
  const messageValid = message.trim().length > 0 && message.length <= MESSAGE_MAX
  const canSubmit = !sending && subjectValid && messageValid && count > 0
  const messageWarn = message.length > MESSAGE_WARN_THRESHOLD

  useEffect(() => {
    if (!open) {
      setSubject(DEFAULT_SUBJECT)
      setMessage(DEFAULT_MESSAGE)
      setSending(false)
    }
  }, [open])

  async function handleSubmit() {
    if (!canSubmit) return
    setSending(true)
    const seq = ++requestSeqRef.current
    try {
      const result = await apiBulkRemindSuppliers({
        supplierIds: selectedSupplierIds,
        subject: subject.trim(),
        message: message.trim(),
      })
      if (seq !== requestSeqRef.current) return

      const { sent, failed, skipped, loggingFailed } = result
      if (sent === 0 && failed === 0 && skipped > 0) {
        toast.info(
          'Aucun fournisseur éligible à la relance (déjà soumis, validés, ou sans campagne active)'
        )
      } else if (failed > 0) {
        toast.error(
          `${sent} réussie${sent > 1 ? 's' : ''}, ${failed} échec${failed > 1 ? 's' : ''}${
            skipped > 0 ? `, ${skipped} ignorée${skipped > 1 ? 's' : ''}` : ''
          }`,
          { duration: Infinity }
        )
      } else {
        toast.success(
          `${sent} relance${sent > 1 ? 's' : ''} envoyée${sent > 1 ? 's' : ''}${
            skipped > 0 ? ` (${skipped} ignorée${skipped > 1 ? 's' : ''})` : ''
          }`
        )
      }

      // Patch P-4 (code review Story 3.2) : prévenir si l'audit-log a
      // échoué (emails envoyés mais CommunicationLog non persisté).
      if (loggingFailed) {
        toast.warning(
          'Relances envoyées, mais journal d\'audit non persisté — contactez un admin',
          { duration: Infinity }
        )
      }

      onSuccess(result)
      if (failed === 0) onOpenChange(false)
    } catch (e: unknown) {
      if (seq !== requestSeqRef.current) return
      const msg = e instanceof Error ? e.message : 'Erreur lors de l\'envoi des relances'
      if (/^API error 401\b/.test(msg) || /^Non authentifié/i.test(msg)) {
        logout()
        return
      }
      toast.error(msg, { duration: Infinity })
    } finally {
      if (seq === requestSeqRef.current) setSending(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && sending) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            Relancer {count} fournisseur{count > 1 ? 's' : ''}
          </DialogTitle>
          <DialogDescription>
            La relance sera envoyée pour chaque campagne active où ce fournisseur n'a pas encore répondu.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md bg-muted p-3 text-sm">
            <span className="text-muted-foreground">De : </span>
            <span className="font-medium">{memberName}</span>{' '}
            <span className="text-muted-foreground">&lt;{memberEmail}&gt;</span>
            <p className="mt-1 text-xs text-muted-foreground">
              L'email sera envoyé depuis l'adresse configurée de votre organisation.
            </p>
          </div>

          {selectedSupplierNames.length > 0 && (
            <div
              className="text-sm text-muted-foreground"
              data-testid="bulk-remind-recipients"
            >
              À : {selectedSupplierNames.join(', ')}
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="bulk-remind-subject">Objet</Label>
            <Input
              id="bulk-remind-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              readOnly={sending}
              autoFocus
              maxLength={SUBJECT_MAX}
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="bulk-remind-message">Message</Label>
            <Textarea
              id="bulk-remind-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              readOnly={sending}
              rows={5}
              maxLength={MESSAGE_MAX}
            />
            <div
              aria-live={messageWarn ? 'polite' : undefined}
              className={
                messageWarn
                  ? 'text-right text-xs text-destructive'
                  : 'text-right text-xs text-muted-foreground'
              }
            >
              {message.length} / {MESSAGE_MAX}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Annuler
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
            {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            Envoyer {count} relance{count > 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
